import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { useContext, useState } from 'react'

import { AnalyticsEvent, StorePersistKeys } from 'src/types'
import { useLocales } from 'src/contexts'

import { useUserInfo } from 'src/hooks/useUserInfo'
import { useManageServiceContacts } from 'src/hooks/useManageServiceContacts'
import { useIsMobile } from 'src/hooks/use-mobile'
import { AnalyticsContext } from 'src/contexts/AnalyticsContext'

import { FormDataContact } from 'src/pages/ServiceContacts/types'
import { getFormFields } from 'src/pages/ServiceContacts/helpers'
import { DEFAULT_VALUES } from 'src/pages/ServiceContacts/constants'
import { queryClient } from 'src/features/AppProviders/constants'

export const useServiceContacts = () => {
  const { translations } = useLocales()
  const isMobile = useIsMobile()
  const {
    userInfo: { favoriteLimit },
  } = useUserInfo()
  const { logEvent } = useContext(AnalyticsContext)

  const { addServiceContacts, isLoadingAddContact, allServiceContacts } =
    useManageServiceContacts()

  const {
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
    getValues,
  } = useForm<FormDataContact>({
    defaultValues: { ...DEFAULT_VALUES },
  })

  const [isFavorite, setIsFavorite] = useState(false)

  const handleFavoriteToggle = (): void => {
    if (!allServiceContacts) return

    const favorites = allServiceContacts.filter(
      contact => contact.isFavorite,
    ).length

    setIsFavorite(prev => {
      const newValue = !prev

      if (newValue && favorites >= favoriteLimit) {
        toast(
          translations.serviceContacts.historyServiceContacts.toast.favorite
            .limit.title,
          {
            description:
              translations.serviceContacts.historyServiceContacts.toast.favorite
                .limit.description,
            action: {
              label:
                translations.serviceContacts.historyServiceContacts.toast
                  .favorite.limit.action.label,
              onClick: () => toast.dismiss(),
            },
          },
        )

        return prev
      }

      setValue('isFavorite', newValue)

      return newValue
    })
  }

  const listOfFields = getFormFields(translations.serviceContacts)

  const onSubmit = (): void => {
    const {
      serviceType,
      contactName,
      phoneNumber,
      address,
      additionalNotes,
      isFavorite,
    } = getValues()

    const newServiceContact: FormDataContact = {
      serviceType,
      contactName: contactName.trim(),
      phoneNumber: phoneNumber.trim(),
      address: address.trim(),
      additionalNotes: additionalNotes.trim(),
      isFavorite,
    }

    addServiceContacts(newServiceContact, {
      onSuccess: () => {
        reset()
        setIsFavorite(false)
        logEvent(AnalyticsEvent.ADD_CONTACT)
        toast(translations.serviceContacts.addServiceContacts.toaster, {
          description:
            translations.serviceContacts.addServiceContacts.toasterBody,
          action: {
            label: 'Okay',
            onClick: () => toast.dismiss(),
          },
        })

        queryClient.invalidateQueries({
          queryKey: [StorePersistKeys.SERVICE_CONTACTS],
        })
      },
    })
  }

  const breadcrumbItems = [
    { labelKey: translations.breadcrumbItems.home, href: '/' },
    {
      labelKey: translations.breadcrumbItems.serviceContacts,
      href: '/service/history',
    },
    { labelKey: translations.breadcrumbItems.add },
  ]

  return {
    locale: translations.serviceContacts.addServiceContacts,
    breadcrumbItems,
    handleSubmit,
    onSubmit,
    control,
    setValue,
    errors,
    listOfFields,
    isLoadingAddContact,
    isFavorite,
    handleFavoriteToggle,
    isMobile,
  }
}
